import * as React from 'react';
import {
  Placement as TypePlacement,
  ReferenceObject as TypeReferenceObject,
} from 'popper.js';
import { Props as BaseProps } from '../Base/Base';
import { ModalContext } from '../Modal/ModalContext';
import PlacementPopper from './PlacementPopper';
import './Placement.css';

export interface Props extends BaseProps {
  /**
   * Flag that enables CSS animations ont he top level
   * transform positioning.
   */
  animatePosition?: boolean;
  /**
   * A render callback function that provides a
   * scheduleUpdate function to trigger a reposition,
   * and the width of the target that is being positioned
   * around.
   */
  children: (props: {
    scheduleUpdate: () => void;
    width?: number;
  }) => React.ReactNode;
  /**
   * Flag that disables repositioning on scroll and resize events.
   */
  disableReposition?: boolean;
  /**
   * Flag that enables flipping the positioned content to the
   * other side when there is not enough room on the screen.
   */
  flip?: boolean;
  /**
   * A callback that is called when the component is
   * in a visible state and the area around it has been clicked on.
   */
  onClose?: (event: MouseEvent) => void;
  /**
   * The desired placement of the positioned content, note that if there
   * is not sufficient room and an a better alternative is available it
   * might not be used.
   */
  placement?: TypePlacement;
  /**
   * An optional reference element to be used as the target.
   */
  referenceElement?: TypeReferenceObject;
  /**
   * The visibilty of the positioned content.
   */
  visible?: boolean;
  /**
   * The desired animation speed on milliseconds.
   */
  timeout?: number;
}

/**
 * The Placement component is used to absolutely position content on the
 * screen around another target. Under the hood it uses the positioning
 * engine library called Popper.js.
 *
 * When the positioning target is another DOM element on the screen the
 * Placement component should be used in conjunction with PlacementManager
 * and PlacementReference. If the target is an existing reference element
 * it can be used by iteself by using the referenceElement prop.
 */
const Placement: React.FunctionComponent<Props> = (props: Props) => {
  return (
    <ModalContext.Consumer>
      { ({ modalElement }) => (
        <PlacementPopper { ...props }
            closeElement={ modalElement }
            isInsideModal={ !!modalElement } />
      ) }
    </ModalContext.Consumer>
  );
};

Placement.defaultProps = {
  flip: true,
  visible: true,
};

export default Placement;
